import axios, { AxiosError, AxiosInstance, AxiosRequestConfig } from "axios";
import i18next from "i18next";

import { BASE_URL, getClientDomain } from "@/configs";
import { getDeviceId } from "@/lib/deviceId";
import { getDeviceProof } from "@/lib/fingerprint";
import { isDeviceProofWasted } from "@/lib/geoAccess";
import { dispatch, logout, store } from "@/store";

/**
 * An error the API layer has already shaped: its message is the player's,
 * and `silent` says it is not worth a toast. A guest's request for something
 * that needs an account is the one case: the page already offers sign-in, and
 * a red "please login" on arrival, over the very buttons that do it, was the
 * first thing a visitor saw on every game.
 */
export class ApiError extends Error {
  silent: boolean;

  constructor(message: string, silent = false) {
    super(message);
    this.name = "ApiError";
    this.silent = silent;
  }
}

const axiosInstance: AxiosInstance = axios.create({
  baseURL: `${BASE_URL}/api/`,
  timeout: 60000,
  // A cross-origin response may only set the session-restore cookie on a credentialed request.
  withCredentials: true,
  headers: {
    "Content-Type": "application/json",
  },
});

// Every fingerprint lookup is billed, so only the requests that start a session carry one.
const SIGN_IN_URL = /^auth\/(login|register|verify-2fa|google|telegram|telegram\/widget|ton)$/;

axiosInstance.interceptors.request.use(
  async (config) => {
    const token = store.getState().auth.token;
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    config.headers["x-domain"] = getClientDomain();
    config.headers["x-device-id"] = getDeviceId();
    if (SIGN_IN_URL.test(config.url ?? "") && !isDeviceProofWasted()) {
      const proof = await getDeviceProof();
      if (proof) config.headers["x-device-proof"] = proof;
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

axiosInstance.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    if (error.response?.status === 401) {
      const wasLoggedIn = store.getState().auth.isLoggedIn;
      dispatch(logout());
      // The default i18next instance is the one locales/i18n.ts set up.
      throw new ApiError(i18next.t("auth.session-expired"), !wasLoggedIn);
    }

    return Promise.reject(error);
  },
);

interface ApiRequest extends AxiosRequestConfig {
  errorMessage: string;
}

const ACCOUNT_INCOMPLETE = "account_incomplete";
// Listened for by the account-completion dialog.
export const ACCOUNT_INCOMPLETE_EVENT = "skystake:account-incomplete";

const API_ERROR_CODES = new Set([
  "country_restricted",
  "anonymizer_restricted",
  "access_restricted",
  ACCOUNT_INCOMPLETE,
  "underage",
  "invalid_date_of_birth",
  "invalid_country",
  "terms_required",
  "date_of_birth_locked",
  "verification_link_invalid",
  "phone_verification_unavailable",
  "phone_invalid",
  "phone_taken",
  "phone_code_limit",
  "phone_code_not_sent",
  "email_change_same_address",
  "email_change_unavailable",
  "email_change_needs_confirmation",
  "email_change_link_invalid",
  "password_required",
  "payment_method_unavailable",
]);

const apiRequest = async <T>(config: ApiRequest): Promise<T> => {
  try {
    const response = await axiosInstance(config);

    return response.data;
  } catch (error) {
    if (error instanceof ApiError) throw error;
    const message =
      error instanceof AxiosError
        ? error.response?.data?.message
        : error instanceof Error
          ? error.message
          : "An unknown error occurred";
    if (message === ACCOUNT_INCOMPLETE) {
      window.dispatchEvent(new CustomEvent(ACCOUNT_INCOMPLETE_EVENT));
    }
    // The compliance errors arrive as machine codes; the player sees the translated text.
    const friendly = API_ERROR_CODES.has(message) ? i18next.t(`error.${message}`) : message;
    // Lets a page tell "does not exist" from "could not reach the server".
    const status = error instanceof AxiosError ? error.response?.status : undefined;
    throw Object.assign(new Error(friendly || config.errorMessage), { status });
  }
};

export default apiRequest;
